import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Spin, Alert } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { useSignUp } from '../context/SignUpContext';
import StepNavigation from '../components/StepNavigation';
import { getCategories, getMccCodes } from '../../../../api/signup';
import { buildMccOptions, resolveMccValue } from '../../../../utils/mcc';

const StepBusinessInfo = () => {
  const [form] = Form.useForm();
  const { formData, updateField, nextStep, prevStep } = useSignUp();
  const [categories, setCategories] = useState([]);
  const [mccCodes, setMccCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesData, mccData] = await Promise.all([
          getCategories(),
          getMccCodes()
        ]);
        console.log('Categories loaded:', categoriesData);
        console.log('MCC loaded:', mccData);

        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setMccCodes(Array.isArray(mccData) ? mccData : []);
        setError(null);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Erro ao carregar categorias. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format currency as user types
  const formatCurrency = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    const amount = parseInt(numbers, 10);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleRevenueChange = (e) => {
    const formatted = formatCurrency(e.target.value);
    form.setFieldValue('revenue', formatted);
    updateField('revenue', e.target.value.replace(/\D/g, ''));
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      nextStep();
    } catch (err) {
      // Validation failed
    }
  };

  if (loading) {
    return (
      <div className="step-content step-loading">
        <Spin size="large" />
        <p>Carregando categorias...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="step-content">
        <Alert
          message="Erro"
          description={error}
          type="error"
          showIcon
        />
        <StepNavigation
          onBack={prevStep}
          showNext={false}
        />
      </div>
    );
  }

  // Build category options
  const categoryOptions = categories.map((cat, idx) => ({
    value: cat.id || cat.code || cat.name || String(idx),
    label: cat.name || cat.label || cat.description || String(cat.id),
  }));

  // Build MCC options with deduplication
  const mccOptions = buildMccOptions(mccCodes);

  return (
    <div className="step-content">
      <div className="step-header">
        <h2>Seu negócio</h2>
        <p>Informe os dados do seu negócio para configurar sua conta.</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          category: formData.category,
          mcc: resolveMccValue(mccOptions, formData.mcc),
          statement_descriptor: formData.statement_descriptor,
          revenue: formData.revenue ? formatCurrency(formData.revenue) : '',
        }}
        requiredMark={false}
      >
        <Form.Item
          name="category"
          label="Categoria do negócio"
          rules={[{ required: true, message: 'Selecione uma categoria' }]}
        >
          <Select
            placeholder="Selecione a categoria"
            size="large"
            options={categoryOptions}
            onChange={(value) => {
              updateField('category', value);
              const selectedOption = categoryOptions.find(opt => opt.value === value);
              updateField('categoryLabel', selectedOption?.label || value);
            }}
          />
        </Form.Item>

        <Form.Item
          name="mcc"
          label="Código de atividade (MCC)"
          rules={[{ required: true, message: 'Selecione um MCC' }]}
          extra="MCC é o código que identifica o tipo de atividade do seu negócio"
        >
          <Select
            placeholder="Selecione o MCC"
            size="large"
            showSearch
            options={mccOptions}
            onChange={(value) => {
              updateField('mcc', value);
              const selectedOption = mccOptions.find(opt => opt.value === value);
              updateField('mccLabel', selectedOption?.label || value);
            }}
            optionFilterProp="label"
            virtual={false}
            listHeight={300}
          />
        </Form.Item>

        <Form.Item
          name="statement_descriptor"
          label="Nome na fatura do cliente"
          rules={[
            { required: true, message: 'Digite o nome que aparecerá na fatura' },
            { max: 13, message: 'Máximo de 13 caracteres' },
          ]}
          extra="Este nome aparecerá na fatura do cartão do cliente (máx. 13 caracteres)"
        >
          <Input
            placeholder="EX: MINHA LOJA"
            size="large"
            maxLength={13}
            onChange={(e) => updateField('statement_descriptor', e.target.value.toUpperCase())}
            style={{ textTransform: 'uppercase' }}
          />
        </Form.Item>

        <Form.Item
          name="revenue"
          label="Faturamento mensal esperado"
          rules={[{ required: true, message: 'Informe o faturamento esperado' }]}
        >
          <Input
            prefix={<DollarOutlined />}
            placeholder="R$ 0"
            size="large"
            onChange={handleRevenueChange}
          />
        </Form.Item>
      </Form>

      <StepNavigation
        onNext={handleNext}
        onBack={prevStep}
      />
    </div>
  );
};

export default StepBusinessInfo;
